import React from 'react'
import { Box, get, muiBankTheme, Space, TextBody } from '@ally/metronome-ui'
import styled from 'styled-components'
import BalanceTable from './BalanceTable'
import BalanceChart from './BalanceChart'
import { useAccountTypes } from '../../../AccountTypeContext'
import WidgetError from '../WidgetError'
import { customErrorAnalytics } from '../../../analytics'

export interface HighlightTableRow {
  accountGroup: string
  highlight: boolean
}

const ChartContainer = styled(Box)`
  flex-basis: 40%;

  ${get.media.down('md')} {
    flex-basis: 100%;
  }
`

const WidgetBody: React.FC = (): React.ReactElement => {
  const [selectedCategory, setSelectedCategory] = React.useState('')
  const {
    groupedAccounts,
    groupedBankAccounts,
    groupedInvestAccounts,
    hasStaleBankAccounts,
    hasStaleInvestAccounts,
    totalBalanceOfBankAndInvest,
    isPartialOutage,
  } = useAccountTypes()

  const updateSelectedCategory = (group: string): void => {
    setSelectedCategory((prevSelectedAcctGrp: string) => {
      return group === prevSelectedAcctGrp ? '' : group
    })
  }

  if (isPartialOutage) {
    customErrorAnalytics(
      "We're having trouble showing your information. We're working on a fix, so check back later.",
    )
    return <WidgetError />
  }

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      flexDirection="column"
      data-testid="accounts-overview-widget-content"
      position="relative"
    >
      <ChartContainer>
        <BalanceChart
          groupedAccounts={groupedAccounts}
          totalBalance={totalBalanceOfBankAndInvest}
          selected={selectedCategory}
          setSelected={updateSelectedCategory}
          theme={muiBankTheme}
          hasStaleData={hasStaleInvestAccounts}
        />
      </ChartContainer>
      <Box>
        {Object.keys(groupedInvestAccounts).length > 0 && (
          <>
            <Space mb="sm">
              <TextBody size="sm" tag="p">
                Invest market data is delayed at least 15 minutes.
              </TextBody>
            </Space>
            <BalanceTable
              groupedAccounts={groupedInvestAccounts}
              accountDomain="INVEST"
              selected={selectedCategory}
              onRowSelect={updateSelectedCategory}
              hasStaleData={hasStaleInvestAccounts}
            />
          </>
        )}

        {Object.keys(groupedBankAccounts).length > 0 && (
          <BalanceTable
            groupedAccounts={groupedBankAccounts}
            accountDomain="BANK"
            selected={selectedCategory}
            onRowSelect={updateSelectedCategory}
            hasStaleData={hasStaleBankAccounts}
          />
        )}
      </Box>
    </Box>
  )
}

export default WidgetBody
